import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, MessageSquare, Plus, Clock, CheckCircle2, 
  HelpCircle, Inbox, XCircle, ChevronRight, Send, Eye, EyeOff
} from 'lucide-react';

const STATUS_CONFIG = {
  'open': { className: 'bg-blue-500/20 text-blue-400', icon: Inbox, label: 'Offen', order: 1 },
  'in_progress': { className: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'In Bearbeitung', order: 2 },
  'waiting_customer': { className: 'bg-purple-500/20 text-purple-400', icon: HelpCircle, label: 'Wartet auf Antwort', order: 3 },
  'resolved': { className: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Gelöst', order: 4 },
  'closed': { className: 'bg-slate-500/20 text-slate-400', icon: XCircle, label: 'Geschlossen', order: 5 },
};

export const PortalSupport = () => {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [showClosed, setShowClosed] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(backendUrl + '/api/portal/tickets', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Tickets error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Bitte fülle alle Felder aus');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(backendUrl + '/api/portal/tickets', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTicket)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Ticket ' + data.ticket_number + ' erstellt');
        setNewTicket({ subject: '', message: '' });
        setCreateOpen(false);
        fetchTickets();
      } else {
        toast.error('Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="portal-support">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Meine Support-Anfragen</h1>
          <p className="text-muted-foreground mt-1">Deine Tickets und Anfragen an den Helden-Support</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Neue Anfrage
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-background border-border">
            <DialogHeader>
              <DialogTitle>Neue Support-Anfrage</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-muted-foreground text-sm mb-2 block">Betreff</label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Worum geht es?"
                  data-testid="new-ticket-subject"
                />
              </div>
              <div>
                <label className="text-muted-foreground text-sm mb-2 block">Deine Nachricht</label>
                <Textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Beschreibe dein Anliegen so detailliert wie möglich..."
                  className="min-h-32"
                  data-testid="new-ticket-message"
                />
              </div>
              <Button 
                onClick={handleCreateTicket} 
                disabled={creating}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="create-ticket-btn"
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Anfrage senden
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : tickets.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="py-16 text-center">
            <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground text-lg mb-2">Keine Support-Anfragen</p>
            <p className="text-muted-foreground/70 text-sm mb-6">
              Du hast noch keine Anfragen an unseren Support gestellt.
            </p>
            <Button onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Erste Anfrage stellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Filter controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch
                id="show-closed"
                checked={showClosed}
                onCheckedChange={setShowClosed}
              />
              <Label htmlFor="show-closed" className="text-sm text-muted-foreground cursor-pointer">
                {showClosed ? (
                  <span className="flex items-center gap-1"><Eye className="h-4 w-4" /> Geschlossene anzeigen</span>
                ) : (
                  <span className="flex items-center gap-1"><EyeOff className="h-4 w-4" /> Geschlossene ausgeblendet</span>
                )}
              </Label>
            </div>
            {!showClosed && tickets.filter(t => t.status === 'closed' || t.status === 'resolved').length > 0 && (
              <span className="text-xs text-muted-foreground">
                {tickets.filter(t => t.status === 'closed' || t.status === 'resolved').length} geschlossene Tickets ausgeblendet
              </span>
            )}
          </div>
          
          {/* Tickets list - filtered and sorted */}
          {tickets
            .filter(ticket => showClosed || (ticket.status !== 'closed' && ticket.status !== 'resolved'))
            .sort((a, b) => {
              // Sort by status order first, then by updated_at
              const orderA = STATUS_CONFIG[a.status]?.order || 99;
              const orderB = STATUS_CONFIG[b.status]?.order || 99;
              if (orderA !== orderB) return orderA - orderB;
              return new Date(b.updated_at) - new Date(a.updated_at);
            })
            .map((ticket) => {
            const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG['open'];
            const StatusIcon = statusConfig.icon;
            return (
              <Link 
                key={ticket.id} 
                to={'/portal/support/' + ticket.id}
                className="block"
              >
                <Card className="glass-card hover:border-primary/50 transition-colors cursor-pointer">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-primary font-mono text-sm font-semibold">
                            {ticket.ticket_number}
                          </span>
                          <Badge className={statusConfig.className}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <h3 className="text-foreground font-medium truncate">{ticket.subject}</h3>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span>{ticket.message_count} Nachricht{ticket.message_count !== 1 ? 'en' : ''}</span>
                          <span>Aktualisiert: {formatDate(ticket.updated_at)}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
          
          {/* Empty state when all visible tickets are filtered out */}
          {!showClosed && tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved').length === 0 && (
            <Card className="glass-card">
              <CardContent className="py-8 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500/50 mb-3" />
                <p className="text-muted-foreground">Alle Anfragen wurden bearbeitet</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowClosed(true)}
                  className="mt-2"
                >
                  Geschlossene Tickets anzeigen
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalSupport;